import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import DashboardBase from "../DashboardBase";
import PanelEstadisticas from "./PanelEstadisticas";
import PsicologoDashboard from "./PsicologoDashboard";
import CargarCompetencias from "./CargarCompetencias";
import { Link } from "react-router-dom";
import { getRolDisplay, hasSupeRadminPrivileges } from "../utils/roles";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarCargadorCompetencias, setMostrarCargadorCompetencias] = useState(false);
  const [stats, setStats] = useState({
    totalEmpresas: 0,
    totalUsuarios: 0,
    evaluacionesPendientes: 0,
    evaluacionesCompletadas: 0,
    nuevasEmpresasEsteMes: 0,
    bajasEmpresasEsteMes: 0
  });
  
  // Verificación adicional de seguridad al nivel del componente
  useEffect(() => {
    const checkAccess = () => {
      // Solo redirigir si el usuario no tiene permisos para estar aquí
      // AdminDashboard es solo para superadmin y admin
      if (userData && userData.rol !== 'superadmin' && userData.rol !== 'admin' && user?.email !== "admin@ossinnovation.com") {
        console.log("� Usuario no es admin/superadmin, redirigiendo a su dashboard correspondiente...");
        
        // Redirigir según el rol del usuario
        if (userData.rol === 'rrhh') {
          navigate('/rrhh');
        } else if (userData.rol === 'psicologo') {
          navigate('/psicologo');
        } else {
          // Para empleados u otros roles
          navigate('/dashboard-empleado');
        }
      }
    };
    
    if (userData) {
      checkAccess();
    }
  }, [userData, navigate, user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
        loadUserData(user);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadUserData = async (user) => {
    try {
      console.log("🔍 Usuario Firebase:", user.email);
      
      // Si es uno de los usuarios con privilegios especiales de superadmin
      if (hasSupeRadminPrivileges(user.email)) {
        console.log("🎯 Detectado ADMIN PRINCIPAL - Forzando rol superadmin");
        const adminData = {
          uid: user.uid,
          email: user.email,
          nombre: user.email === "db@ossinnovation.com" ? "basededatos" : "Administrador",
          apellido: user.email === "db@ossinnovation.com" ? "datos" : "Sistema", 
          rol: "superadmin",
          estado: "activo",
          fechaCreacion: new Date()
        };
        console.log("✅ ACCESO AUTORIZADO - Admin principal con rol superadmin");
        setUserData(adminData);
        await loadDashboardData(adminData);
        
        // Agregar delay mínimo para mostrar la pantalla de carga
        await new Promise(resolve => setTimeout(resolve, 1500));
        return;
      }

      // Buscar datos del usuario actual
      const q = query(collection(db, "usuarios"), where("uid", "==", user.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = { id: userDoc.id, ...userDoc.data() };
        console.log("🔍 Datos del usuario cargados:", userData);
        setUserData(userData);
        
        // CONTROL DE ACCESO: Redirigir usuarios según su rol
        if (userData.rol === 'rrhh') {
          console.log("🔄 Usuario RRHH detectado, redirigiendo...");
          navigate('/rrhh');
          return;
        } else if (userData.rol === 'psicologo') {
          console.log("� Usuario Psicólogo detectado, redirigiendo...");
          navigate('/psicologo');
          return;
        } else if (userData.rol !== 'superadmin' && userData.rol !== 'admin') {
          console.log("🔄 Usuario empleado detectado, redirigiendo...");
          navigate('/dashboard-empleado');
          return;
        }
        
        // Redirigir según el rol
        if (userData.rol === 'psicologo') {
          return <PsicologoDashboard />;
        }
        
        await loadDashboardData(userData);
        
        // Agregar delay mínimo para mostrar la pantalla de carga
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    } catch (error) {
      console.error("Error al cargar datos del usuario:", error);
      // Agregar delay incluso en caso de error
      await new Promise(resolve => setTimeout(resolve, 1500));
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async (userData) => {
    try {
      console.log("📊 Cargando datos del dashboard para rol:", userData.rol);
      let empresasData = [];
      let usuariosData = [];
      let evaluacionesData = [];

      if (userData.rol === 'superadmin') {
        console.log("🏢 Cargando datos para SUPERADMIN");
        // SuperAdmin ve todo
        try {
          const empresasSnapshot = await getDocs(collection(db, "empresas"));
          empresasData = empresasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log("📊 Empresas cargadas:", empresasData.length, empresasData);
        } catch (error) {
          console.log("⚠️ Error cargando empresas:", error.message);
        }
        
        try {
          const usuariosSnapshot = await getDocs(collection(db, "usuarios"));
          usuariosData = usuariosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log("👥 Usuarios cargados:", usuariosData.length, usuariosData);
        } catch (error) {
          console.log("⚠️ Error cargando usuarios:", error.message);
        }
        
        try {
          const evaluacionesSnapshot = await getDocs(collection(db, "evaluaciones"));
          evaluacionesData = evaluacionesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log("📋 Evaluaciones cargadas:", evaluacionesData.length, evaluacionesData);
        } catch (error) {
          console.log("⚠️ Error cargando evaluaciones (probablemente no existe la colección):", error.message);
          evaluacionesData = []; // Usar array vacío si no existe la colección
        }
        
      } else {
        // Admin, RRHH, Psicólogo ven solo su empresa
        if (userData.empresaId) {
          const empresaSnapshot = await getDocs(
            query(collection(db, "empresas"), where("id", "==", userData.empresaId))
          );
          empresasData = empresaSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          const usuariosSnapshot = await getDocs(
            query(collection(db, "usuarios"), where("empresaId", "==", userData.empresaId))
          );
          usuariosData = usuariosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          const evaluacionesSnapshot = await getDocs(
            query(collection(db, "evaluaciones"), where("empresaId", "==", userData.empresaId))
          );
          evaluacionesData = evaluacionesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }
      }

      setEmpresas(empresasData);
      setUsuarios(usuariosData);
      setEvaluaciones(evaluacionesData);

      // Obtener fechas de este mes
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      // Calcular estadísticas
      let nuevasEmpresasEsteMes = 0;
      let bajasEmpresasEsteMes = 0;

      if (userData.rol === 'superadmin') {
        // Calcular empresas nuevas este mes
        nuevasEmpresasEsteMes = empresasData.filter(empresa => {
          if (empresa.fechaCreacion) {
            const fechaCreacion = empresa.fechaCreacion.toDate ? empresa.fechaCreacion.toDate() : new Date(empresa.fechaCreacion);
            return fechaCreacion >= startOfMonth && fechaCreacion <= endOfMonth;
          }
          return false;
        }).length;

        // Calcular bajas este mes (empresas con estado inactivo este mes)
        bajasEmpresasEsteMes = empresasData.filter(empresa => {
          if (empresa.fechaBaja && empresa.estado === 'inactivo') {
            const fechaBaja = empresa.fechaBaja.toDate ? empresa.fechaBaja.toDate() : new Date(empresa.fechaBaja);
            return fechaBaja >= startOfMonth && fechaBaja <= endOfMonth;
          }
          return false;
        }).length;
      }

      setStats({
        totalEmpresas: empresasData.length,
        totalUsuarios: usuariosData.filter(u => u.estado === 'activo').length,
        evaluacionesPendientes: evaluacionesData.filter(e => e.estado === 'pendiente').length,
        evaluacionesCompletadas: evaluacionesData.filter(e => e.estado === 'completada').length,
        nuevasEmpresasEsteMes,
        bajasEmpresasEsteMes
      });

      console.log("📈 Estadísticas calculadas:", {
        totalEmpresas: empresasData.length,
        totalUsuarios: usuariosData.filter(u => u.estado === 'activo').length,
        evaluacionesPendientes: evaluacionesData.filter(e => e.estado === 'pendiente').length,
        evaluacionesCompletadas: evaluacionesData.filter(e => e.estado === 'completada').length,
        nuevasEmpresasEsteMes,
        bajasEmpresasEsteMes
      });

    } catch (error) {
      console.error("Error al cargar datos del dashboard:", error);
    }
  };

  // Función para imprimir todas las preguntas de la base de datos
  const imprimirTodasLasPreguntas = async () => {
    try {
      console.log("📋 Iniciando carga de todas las preguntas...");
      
      // Mostrar indicador de carga
      const loadingElement = document.createElement('div');
      loadingElement.id = 'preguntas-loading';
      loadingElement.innerHTML = `
        <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
          <div style="background: white; padding: 2rem; border-radius: 8px; text-align: center; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
            <div style="font-size: 2rem; margin-bottom: 1rem;">📋</div>
            <div style="font-weight: 600; color: #1e293b; margin-bottom: 0.5rem;">Cargando preguntas...</div>
            <div style="color: #6b7280; font-size: 0.875rem;">Por favor espera un momento</div>
          </div>
        </div>
      `;
      document.body.appendChild(loadingElement);

      // Obtener todas las competencias
      const competenciasSnapshot = await getDocs(collection(db, "competencias"));
      const competenciasData = competenciasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      console.log("✅ Competencias cargadas:", competenciasData.length);

      // Clasificar competencias por tipo
      const comportamentales = competenciasData.filter(comp => comp.tipoCompetencia === 'comportamental');
      const liderazgo = competenciasData.filter(comp => comp.tipoCompetencia === 'liderazgo');
      const funcionales = competenciasData.filter(comp => comp.tipoCompetencia === 'funcional');

      console.log("📊 Clasificación:", {
        comportamentales: comportamentales.length,
        liderazgo: liderazgo.length,
        funcionales: funcionales.length
      });

      // Crear elemento temporal para el contenido del PDF
      const element = document.createElement('div');
      element.style.padding = '20px';
      element.style.fontFamily = 'Arial, sans-serif';
      element.style.backgroundColor = 'white';
      element.style.lineHeight = '1.6';
      element.style.fontSize = '14px';
      
      const fechaActual = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      let contenido = `
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px;">
          <h1 style="color: #1e293b; margin: 0; font-size: 24px;">📋 Listado Completo de Preguntas</h1>
          <h2 style="color: #3b82f6; margin: 10px 0; font-size: 18px;">Sistema de Evaluación 180°</h2>
          <p style="color: #6b7280; margin: 5px 0; font-size: 12px;">Generado el ${fechaActual}</p>
          <p style="color: #6b7280; margin: 5px 0; font-size: 12px;">Total de competencias: ${competenciasData.length}</p>
        </div>

        <div style="margin-bottom: 30px;">
          <h2 style="color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; margin-bottom: 20px;">
            🧠 COMPETENCIAS COMPORTAMENTALES (${comportamentales.length})
          </h2>
          ${comportamentales.map((comp, index) => `
            <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px;">
              <h3 style="color: #3b82f6; margin: 0 0 10px 0; font-size: 16px;">
                ${index + 1}. ${comp.nombre}
              </h3>
              <p style="color: #6b7280; margin: 0 0 10px 0; font-style: italic;">
                ${comp.descripcion || 'Sin descripción'}
              </p>
              <div style="background-color: #f8fafc; padding: 10px; border-radius: 4px;">
                <strong style="color: #374151;">Categoría:</strong> ${comp.categoria || 'No especificada'}<br>
                <strong style="color: #374151;">Tipo:</strong> Comportamental<br>
                ${comp.requiereLiderazgo ? '<span style="color: #f59e0b;">🔸 Requiere liderazgo</span><br>' : ''}
                ${comp.empresa_id ? `<strong style="color: #374151;">Empresa:</strong> ${comp.empresa_id}<br>` : ''}
              </div>
            </div>
          `).join('')}
        </div>

        <div style="margin-bottom: 30px;">
          <h2 style="color: #1e293b; border-bottom: 2px solid #f59e0b; padding-bottom: 10px; margin-bottom: 20px;">
            👑 COMPETENCIAS DE LIDERAZGO (${liderazgo.length})
          </h2>
          ${liderazgo.map((comp, index) => `
            <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px;">
              <h3 style="color: #f59e0b; margin: 0 0 10px 0; font-size: 16px;">
                ${index + 1}. ${comp.nombre}
              </h3>
              <p style="color: #6b7280; margin: 0 0 10px 0; font-style: italic;">
                ${comp.descripcion || 'Sin descripción'}
              </p>
              <div style="background-color: #fffbeb; padding: 10px; border-radius: 4px;">
                <strong style="color: #374151;">Categoría:</strong> ${comp.categoria || 'No especificada'}<br>
                <strong style="color: #374151;">Tipo:</strong> Liderazgo<br>
                ${comp.cargoEspecifico ? `<strong style="color: #374151;">Cargo específico:</strong> ${comp.cargoEspecifico}<br>` : ''}
                ${comp.empresa_id ? `<strong style="color: #374151;">Empresa:</strong> ${comp.empresa_id}<br>` : ''}
              </div>
            </div>
          `).join('')}
        </div>

        <div style="margin-bottom: 30px;">
          <h2 style="color: #1e293b; border-bottom: 2px solid #16a34a; padding-bottom: 10px; margin-bottom: 20px;">
            🏢 COMPETENCIAS FUNCIONALES (${funcionales.length})
          </h2>
          ${funcionales.map((comp, index) => `
            <div style="margin-bottom: 20px; padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px;">
              <h3 style="color: #16a34a; margin: 0 0 10px 0; font-size: 16px;">
                ${index + 1}. ${comp.nombre}
              </h3>
              <p style="color: #6b7280; margin: 0 0 10px 0; font-style: italic;">
                ${comp.descripcion || 'Sin descripción'}
              </p>
              <div style="background-color: #f0fdf4; padding: 10px; border-radius: 4px;">
                <strong style="color: #374151;">Categoría:</strong> ${comp.categoria || 'No especificada'}<br>
                <strong style="color: #374151;">Tipo:</strong> Funcional<br>
                ${comp.cargoEspecifico ? `<strong style="color: #374151;">Cargo específico:</strong> ${comp.cargoEspecifico}<br>` : ''}
                <strong style="color: #374151;">Empresa:</strong> ${comp.empresa_id || 'No especificada'}<br>
              </div>
            </div>
          `).join('')}
        </div>

        <!-- Resumen final -->
        <div style="margin-top: 30px; padding: 20px; background-color: #f8fafc; border-radius: 8px; border: 2px solid #e5e7eb;">
          <h3 style="color: #1e293b; margin: 0 0 15px 0; text-align: center;">📊 Resumen</h3>
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; text-align: center;">
            <div>
              <div style="font-size: 24px; font-weight: bold; color: #3b82f6;">${comportamentales.length}</div>
              <div style="color: #6b7280; font-size: 12px;">Comportamentales</div>
            </div>
            <div>
              <div style="font-size: 24px; font-weight: bold; color: #f59e0b;">${liderazgo.length}</div>
              <div style="color: #6b7280; font-size: 12px;">Liderazgo</div>
            </div>
            <div>
              <div style="font-size: 24px; font-weight: bold; color: #16a34a;">${funcionales.length}</div>
              <div style="color: #6b7280; font-size: 12px;">Funcionales</div>
            </div>
          </div>
          <div style="text-align: center; margin-top: 15px; font-weight: bold; color: #1e293b;">
            Total: ${competenciasData.length} competencias
          </div>
        </div>

        <!-- Pie de página -->
        <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center;">
          <p style="color: #6b7280; font-size: 10px; margin: 5px 0;">
            © ${new Date().getFullYear()} Sistema de Evaluación 180° - Documento confidencial
          </p>
          <p style="color: #6b7280; font-size: 10px; margin: 5px 0;">
            Generado por: ${userData?.nombre || 'Administrador'} (${userData?.rol || 'Admin'})
          </p>
        </div>
      `;

      element.innerHTML = contenido;
      document.body.appendChild(element);

      // Esperar un poco para que se renderice
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Imprimir directamente (sin estilos adicionales como solicitas)
      window.print();

      // Limpiar
      setTimeout(() => {
        try {
          document.body.removeChild(element);
          const loadingEl = document.getElementById('preguntas-loading');
          if (loadingEl) {
            document.body.removeChild(loadingEl);
          }
        } catch (error) {
          console.log("Error limpiando elementos:", error);
        }
      }, 1000);

      console.log("✅ Impresión de preguntas completada");

    } catch (error) {
      console.error("❌ Error al imprimir preguntas:", error);
      alert("Error al cargar las preguntas. Inténtalo de nuevo.");
      
      // Limpiar loading en caso de error
      const loadingEl = document.getElementById('preguntas-loading');
      if (loadingEl) {
        document.body.removeChild(loadingEl);
      }
    }
  };

  // La función getRolDisplay ahora se importa desde utils/roles.js

  const getEmpresaNombre = (empresaId) => {
    const empresa = empresas.find(e => e.id === empresaId);
    return empresa ? empresa.nombre : "Sin empresa";
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
        color: 'white'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          borderRadius: '16px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px'
          }}></div>
          <h2 style={{ 
            margin: '0 0 12px 0', 
            fontSize: '24px',
            fontWeight: '600'
          }}>
            Elipse 360°
          </h2>
          <p style={{ 
            margin: '0 0 8px 0', 
            fontSize: '16px',
            opacity: '0.9'
          }}>
            Cargando Panel de Administración...
          </p>
          <p style={{ 
            margin: 0, 
            fontSize: '14px',
            opacity: '0.7'
          }}>
            Verificando permisos y cargando datos del sistema
          </p>
        </div>
        
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Verificación final de acceso antes del render
  // Solo mostrar "Acceso Denegado" si hay un usuario logueado pero sin permisos
  if (userData && userData.rol !== 'superadmin' && !hasSupeRadminPrivileges(user?.email)) {
    return (
      <DashboardBase title="Acceso Denegado" activeNav="dashboard">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          textAlign: 'center',
          color: '#dc2626'
        }}>
          <div style={{ fontSize: '4em', marginBottom: '16px' }}>🚫</div>
          <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Acceso Denegado</h2>
          <p style={{ color: '#6b7280', marginBottom: '24px', maxWidth: '500px' }}>
            Solo los usuarios con rol de <strong>Super Administrador</strong> pueden acceder a este panel.
            Tu rol actual es: <strong>{getRolDisplay(userData.rol)}</strong>
          </p>
          <button 
            onClick={() => navigate('/dashboard')}
            style={{
              background: '#1e40af',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Volver al Dashboard
          </button>
        </div>
      </DashboardBase>
    );
  }

  return (
    <DashboardBase 
      title={userData?.rol === 'superadmin' ? 'Panel de Administración Elipse 360' : `Panel ${getRolDisplay(userData?.rol)}`}
      userName={userData ? `${userData.nombre} ${userData.apellido}` : "Usuario"}
      userRole={getRolDisplay(userData?.rol)}
      activeNav="dashboard"
    >
      <div className="content-area">
        {/* Bienvenida personalizada */}
        <div style={{
          background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
          color: 'white',
          padding: '24px',
          borderRadius: '12px',
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '1.8em', fontWeight: '700', marginBottom: '8px' }}>
            ¡Bienvenido, {userData?.nombre}!
          </h1>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '1.1em' }}>
            {userData?.rol === 'superadmin' 
              ? 'Gestiona todas las empresas y usuarios del sistema'
              : `Gestiona ${getEmpresaNombre(userData?.empresaId)}`
            }
          </p>
        </div>

        {/* Estadísticas principales */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '2em' }}>🏢</span>
              <h3 style={{ margin: 0 }}>
                {userData?.rol === 'superadmin' ? 'Empresas Registradas' : 'Mi Empresa'}
              </h3>
            </div>
            <div style={{ fontSize: '2.5em', fontWeight: '700', marginBottom: '8px' }}>
              {stats.totalEmpresas}
            </div>
            <p style={{ margin: 0, opacity: 0.9 }}>
              {userData?.rol === 'superadmin' ? 'En el sistema' : 'Registrada'}
            </p>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
            color: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '2em' }}>👥</span>
              <h3 style={{ margin: 0 }}>Usuarios Activos</h3>
            </div>
            <div style={{ fontSize: '2.5em', fontWeight: '700', marginBottom: '8px' }}>
              {stats.totalUsuarios}
            </div>
            <p style={{ margin: 0, opacity: 0.9 }}>En el sistema</p>
          </div>

          {userData?.rol === 'superadmin' ? (
            <>
              <div style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '2em' }}>🆕</span>
                  <h3 style={{ margin: 0 }}>Nuevas Empresas</h3>
                </div>
                <div style={{ fontSize: '2.5em', fontWeight: '700', marginBottom: '8px' }}>
                  {stats.nuevasEmpresasEsteMes}
                </div>
                <p style={{ margin: 0, opacity: 0.9 }}>Este mes</p>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '2em' }}>📉</span>
                  <h3 style={{ margin: 0 }}>Bajas de Empresas</h3>
                </div>
                <div style={{ fontSize: '2.5em', fontWeight: '700', marginBottom: '8px' }}>
                  {stats.bajasEmpresasEsteMes}
                </div>
                <p style={{ margin: 0, opacity: 0.9 }}>Este mes</p>
              </div>
            </>
          ) : (
            <>
              <div style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '2em' }}>⏳</span>
                  <h3 style={{ margin: 0 }}>Evaluaciones Pendientes</h3>
                </div>
                <div style={{ fontSize: '2.5em', fontWeight: '700', marginBottom: '8px' }}>
                  {stats.evaluacionesPendientes}
                </div>
                <p style={{ margin: 0, opacity: 0.9 }}>Por completar</p>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '2em' }}>✅</span>
                  <h3 style={{ margin: 0 }}>Evaluaciones Completadas</h3>
                </div>
                <div style={{ fontSize: '2.5em', fontWeight: '700', marginBottom: '8px' }}>
                  {stats.evaluacionesCompletadas}
                </div>
                <p style={{ margin: 0, opacity: 0.9 }}>Este período</p>
              </div>
            </>
          )}
        </div>

        {/* Accesos rápidos - Dashboard SuperAdmin */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {/* Gestión de Empresas */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🏢 Gestión de Empresas</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {userData?.rol === 'superadmin' && (
                <>
                  <button
                    onClick={() => window.location.href = '/empresas'}
                    className="btn btn-outline"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    🏢 Gestionar Empresas
                  </button>
                  <button
                    onClick={() => window.location.href = '/empresas/crear'}
                    className="btn btn-outline"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    ➕ Crear Nueva Empresa
                  </button>
                  <button
                    onClick={() => window.location.href = '/empresas/departamentos'}
                    className="btn btn-outline"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    🏛️ Gestionar Departamentos
                  </button>
                  <button
                    onClick={() => window.location.href = '/empresas/cargos'}
                    className="btn btn-outline"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    � Gestionar Cargos
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Gestión de Personal */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">�👥 Gestión de Personal</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => window.location.href = '/usuarios'}
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
              >
                👥 Gestionar Usuarios
              </button>
              <button
                onClick={() => window.location.href = '/empleados'}
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
              >
                👤 Gestionar Empleados
              </button>
              <button
                onClick={() => window.location.href = '/usuarios/crear'}
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
              >
                ➕ Crear Usuario
              </button>
              <button
                onClick={() => window.location.href = '/empleados/crear'}
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
              >
                ➕ Crear Empleado
              </button>
            </div>
          </div>

          {/* Sistema de Evaluaciones */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">📝 Sistema de Evaluaciones</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => window.location.href = '/evaluaciones'}
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
              >
                📝 Gestión de Evaluaciones
              </button>
              <button
                onClick={() => window.location.href = '/evaluaciones/plantillas'}
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
              >
                📋 Plantillas de Evaluación
              </button>
              <button
                onClick={() => window.location.href = '/evaluaciones/competencias'}
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
              >
                🎯 Gestión de Competencias
              </button>
              <button
                onClick={() => window.location.href = '/evaluaciones/calificacion'}
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
              >
                📊 Plantillas de Calificación
              </button>
            </div>
          </div>

          {/* Herramientas Avanzadas */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🔧 Herramientas Avanzadas</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {userData?.rol === 'superadmin' && (
                <>
                  <button
                    onClick={() => setMostrarCargadorCompetencias(!mostrarCargadorCompetencias)}
                    className="btn btn-outline"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    📚 {mostrarCargadorCompetencias ? 'Ocultar' : 'Cargar'} Competencias
                  </button>
                  <button
                    onClick={() => window.location.href = '/evaluaciones/escalas'}
                    className="btn btn-outline"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    📈 Escalas Likert
                  </button>
                  <button
                    onClick={() => window.location.href = '/reportes'}
                    className="btn btn-outline"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    📊 Reportes y Estadísticas
                  </button>
                  <button
                    onClick={imprimirTodasLasPreguntas}
                    className="btn btn-outline"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    📋 Imprimir Todas las Preguntas
                  </button>
                  <button
                    onClick={() => window.location.href = '/configuracion'}
                    className="btn btn-outline"
                    style={{ justifyContent: 'flex-start' }}
                  >
                    ⚙️ Configuración del Sistema
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Resumen de Actividad Mejorado */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">📊 Resumen de Actividad</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                background: '#f8fafc',
                borderRadius: '8px'
              }}>
                <span style={{ color: '#64748b' }}>Usuarios administradores:</span>
                <span style={{ fontWeight: '600', color: '#1e40af' }}>
                  {usuarios.filter(u => ['superadmin', 'admin', 'rrhh', 'psicologo'].includes(u.rol)).length}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                background: '#f8fafc',
                borderRadius: '8px'
              }}>
                <span style={{ color: '#64748b' }}>Empleados/Evaluadores:</span>
                <span style={{ fontWeight: '600', color: '#10b981' }}>
                  {usuarios.filter(u => ['supervisor', 'evaluador'].includes(u.rol)).length}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                background: '#f8fafc',
                borderRadius: '8px'
              }}>
                <span style={{ color: '#64748b' }}>Tasa de completitud:</span>
                <span style={{ fontWeight: '600', color: '#f59e0b' }}>
                  {stats.evaluacionesCompletadas + stats.evaluacionesPendientes > 0 
                    ? Math.round((stats.evaluacionesCompletadas / (stats.evaluacionesCompletadas + stats.evaluacionesPendientes)) * 100)
                    : 0}%
                </span>
              </div>
              {userData?.rol === 'superadmin' && (
                <>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#f0fdf4',
                    borderRadius: '8px'
                  }}>
                    <span style={{ color: '#064e3b' }}>Empresas activas:</span>
                    <span style={{ fontWeight: '600', color: '#059669' }}>
                      {empresas.filter(e => e.estado === 'activo').length}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    background: '#fef2f2',
                    borderRadius: '8px'
                  }}>
                    <span style={{ color: '#7f1d1d' }}>Empresas inactivas:</span>
                    <span style={{ fontWeight: '600', color: '#dc2626' }}>
                      {empresas.filter(e => e.estado === 'inactivo').length}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Acciones Rápidas Adicionales */}
        {userData?.rol === 'superadmin' && (
          <div className="card" style={{ marginBottom: '32px' }}>
            <div className="card-header">
              <h3 className="card-title">⚡ Acciones Rápidas del Sistema</h3>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <button
                onClick={() => window.location.href = '/evaluaciones/activar'}
                className="btn btn-primary"
                style={{ 
                  padding: '16px', 
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{ fontSize: '24px' }}>🚀</span>
                <span>Activar Evaluaciones</span>
              </button>
              <button
                onClick={() => window.location.href = '/evaluaciones/manual-funciones'}
                className="btn btn-primary"
                style={{ 
                  padding: '16px', 
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{ fontSize: '24px' }}>💼</span>
                <span>Manual de Funciones</span>
              </button>
              <button
                onClick={() => window.location.href = '/backup'}
                className="btn btn-outline"
                style={{ 
                  padding: '16px', 
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{ fontSize: '24px' }}>💾</span>
                <span>Backup Sistema</span>
              </button>
              <button
                onClick={() => window.location.href = '/logs'}
                className="btn btn-outline"
                style={{ 
                  padding: '16px', 
                  height: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{ fontSize: '24px' }}>📋</span>
                <span>Logs del Sistema</span>
              </button>
            </div>
          </div>
        )}

        {/* Lista de empresas (solo para SuperAdmin) */}
        {userData?.rol === 'superadmin' && empresas.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🏢 Empresas en el Sistema</h3>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              {empresas.map((empresa) => (
                <div key={empresa.id} style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  transition: 'all 0.2s ease'
                }}>
                  <h4 style={{
                    fontSize: '1.1em',
                    fontWeight: '600',
                    color: '#1e293b',
                    margin: '0 0 8px 0'
                  }}>
                    {empresa.nombre}
                  </h4>
                  <p style={{ color: '#64748b', fontSize: '0.9em', margin: '0 0 8px 0' }}>
                    RUC: {empresa.ruc}
                  </p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '0.8em',
                      fontWeight: '500',
                      backgroundColor: empresa.estado === 'activo' ? '#dcfce7' : '#fee2e2',
                      color: empresa.estado === 'activo' ? '#166534' : '#991b1b'
                    }}>
                      {empresa.estado}
                    </span>
                    <span style={{ color: '#64748b', fontSize: '0.9em' }}>
                      {usuarios.filter(u => u.empresaId === empresa.id).length} usuarios
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cargador de Competencias */}
        {mostrarCargadorCompetencias && userData?.rol === 'superadmin' && (
          <CargarCompetencias />
        )}
      </div>
    </DashboardBase>
  );
};

export default AdminDashboard;
